/**
 * @fileoverview System-web telemetry bootstrap — initializes the shared browser
 * telemetry client for the platform admin portal.
 * @module system-web/telemetry
 *
 * Responsibilities:
 * - Own the system-web Application Insights singleton (RUM + offline buffer + CORS
 *   correlation) via the shared @firesquirrel/web-telemetry package.
 * - Boot telemetry once, after runtime config is resolved.
 * Dependencies:
 * - @firesquirrel/web-telemetry — SDK bootstrap / buffer / enrichment / sampling.
 * - ../config — runtime/build-time system-web config (connection string, appEnv).
 * Architecture:
 * - Cloud role 'system-web' so browser telemetry is a distinct node in the App Map.
 *   No API calls here — cross-cutting observability infra, not UI data access.
 * Notes:
 * - initTelemetry() never throws; telemetry is disabled gracefully when no connection
 *   string is configured. Safe to call once at boot.
 */
import { WebTelemetry } from '@firesquirrel/web-telemetry';
import { getWebConfig } from '../config';

/** Singleton system-web telemetry client. */
export const telemetry = new WebTelemetry('firesquirrel-system-telemetry-buffer');

/**
 * @process initTelemetry
 * Steps:   read system-web config → initialize the shared SDK with cloudRole
 *          'system-web' + env-aware sampling + full RUM/CORS correlation.
 * Inputs:  none (reads getWebConfig()).
 * Outputs: Promise<void>.
 * Failure Handling: delegated to WebTelemetry.initialize (never throws).
 * Idempotency: REQUIRED (repeat calls are no-ops after the first).
 */
export async function initTelemetry(): Promise<void> {
  const { appEnv, appInsightsConnectionString } = getWebConfig();
  await telemetry.initialize({
    connectionString: appInsightsConnectionString,
    appEnv,
    cloudRole: 'system-web',
    appVersion: import.meta.env.VITE_BUILD_VERSION || 'dev',
  });
}
